/* eslint-disable max-len, react/no-danger */
import { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { navigate } from 'gatsby-link';

import Link from '../../Link';
import ScrollHint from '../../ScrollHint';
import renderIcon from '../../../utils/renderIcon';
import { useScrollHint } from '../../../utils/hooks';

import {
	suggestProducts, suggestContent, suggestVideos, PRODUCTS_INDEX
} from '../helpers/algolia-api';
import { mapSuggestHit, sendProductClickEvent, storeSearchClickContext } from '../helpers/algolia-functions';
import { getRecentSearches, addRecentSearch, clearRecentSearches } from '../helpers/recent-searches';

const ns = 'search-bar-algolia';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;
const SUGGEST_HITS = 6;
const CONTENT_SUGGEST_HITS = 3;
const VIDEO_SUGGEST_HITS = 3;
const EMPTY_SUGGESTIONS = { products: [], content: [], videos: [] };
//	Search Suggestions are split into labelled groups.
const SUGGESTION_GROUPS = [
	{ key: 'products', label: 'Products' },
	{ key: 'content', label: 'Content' },
	{ key: 'videos', label: 'Videos' },
];

//	Quick links column of the empty-state panel (desktop only, per design).
const QUICK_LINKS = [
	{ label: 'Education Finder', url: '/pilates-training-near-me/' },
	{ label: 'Studio Finder', url: '/studio-finder/' },
	{ label: 'Events', url: '/pilates-events-continuing-education' },
	{ label: 'How to Choose', url: '/how-to-choose-pilates-reformer' },
	{ label: 'Reformer Comparison', url: '/comparisons/pilates-reformers/' },
];

//	Curated fallback for the empty-state "Popular searches" column. Swap for an
//	Algolia Query Suggestions index here if/when one is available.
const POPULAR_SEARCHES = [
	'Reformer',
	'Springboard',
	'Allegro',
	'Springs',
	'Pilates Ring',
	'Barrel',
	'Chair',
	'Mat',
	'Trapeze',
];

const SearchBar = ({ isSearchFlyout, closeSearchFlyout = () => {}, activeResultsQuery }) => {
	const [query, setQuery] = useState('');
	const [recent, setRecent] = useState([]);
	const [products, setProducts] = useState([]);
	const [suggestions, setSuggestions] = useState(EMPTY_SUGGESTIONS);
	const [loading, setLoading] = useState(false);

	const {
		panelRef, showScrollHint, updateScrollHint, handleScrollHintClick,
	} = useScrollHint(isSearchFlyout);

	const inputRef = useRef();
	const debounceRef = useRef();
	const queryRef = useRef(query);

	const trimmed = query.trim();
	const isTyping = trimmed.length >= MIN_CHARS;

	//	The panel's content changes as the shopper types (recent, suggestions, products),
	//	so re-check whether there is more to scroll to.
	useEffect(() => {
		updateScrollHint();
	}, [isTyping, recent, products, suggestions, loading]); // eslint-disable-line

	//	Focus the input and refresh recent searches whenever the flyout opens.
	useEffect(() => {
		if (!isSearchFlyout) return;
		setRecent(getRecentSearches());
		const id = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 100);
		return () => clearTimeout(id);
	}, [isSearchFlyout]);

	useEffect(() => {
		queryRef.current = query;
	}, [query]);

	//	Keeps this field from showing a stale term after a new search happens
	//	elsewhere (e.g. the results page's own search box, not this flyout).
	//	If the new activeResultsQuery already matches what's typed here, this
	//	flyout caused the change itself (via runSearch below) and already
	//	matches -- otherwise clear it, rather than showing a term the shopper
	//	never actually typed into the flyout.
	useEffect(() => {
		if (queryRef.current === (activeResultsQuery || '')) return;
		setQuery('');
		setProducts([]);
		setSuggestions(EMPTY_SUGGESTIONS);
	}, [activeResultsQuery]); // eslint-disable-line

	//	Debounced suggest. Only queries once the input has >= MIN_CHARS.
	useEffect(() => {
		clearTimeout(debounceRef.current);

		if (!isTyping) {
			setProducts([]);
			setSuggestions(EMPTY_SUGGESTIONS);
			setLoading(false);
			return undefined;
		}

		setLoading(true);
		debounceRef.current = setTimeout(async() => {
			try {
				//	A content/video failure must not take the product suggestions down with it.
				const [res, contentRes, videosRes] = await Promise.all([
					suggestProducts({ query: trimmed, hitsPerPage: SUGGEST_HITS }),
					suggestContent({ query: trimmed, hitsPerPage: CONTENT_SUGGEST_HITS }).catch(() => ({ hits: [] })),
					suggestVideos({ query: trimmed, hitsPerPage: VIDEO_SUGGEST_HITS }).catch(() => ({ hits: [] })),
				]);
				const hits = res.hits || [];

				setProducts(hits.map((hit, hitIndex) => mapSuggestHit(hit, { index: hitIndex, queryID: res.queryID })));

				//	Search suggestions = matching product, content (pages/blog)
				//	and video titles, grouped, each with the query highlighted (deduped by title, capped).
				const seen = new Set();
				const toSuggestions = (list, limit) => list.reduce((acc, hit) => {
					const { title } = hit;
					if (!title || seen.has(title.toLowerCase()) || acc.length >= limit) return acc;
					seen.add(title.toLowerCase());
					acc.push({
						title,
						highlighted: hit._highlightResult?.title?.value || title,
					});
					return acc;
				}, []);
				setSuggestions({
					products: toSuggestions(hits, SUGGEST_HITS),
					content: toSuggestions(contentRes.hits || [], CONTENT_SUGGEST_HITS),
					videos: toSuggestions(videosRes.hits || [], VIDEO_SUGGEST_HITS),
				});
			} catch (e) {
				setProducts([]);
				setSuggestions(EMPTY_SUGGESTIONS);
			} finally {
				setLoading(false);
			}
		}, DEBOUNCE_MS);

		return () => clearTimeout(debounceRef.current);
	}, [trimmed, isTyping]);

	const runSearch = (term, tab) => {
		const clean = (term || '').trim();
		if (!clean) return;
		setRecent(addRecentSearch(clean));
		closeSearchFlyout();
		navigate(`/search?q=${ encodeURIComponent(clean) }${ tab ? `&tab=${ tab }` : '' }`, { replace: true });
	};

	const handleSubmit = e => {
		e.preventDefault();
		runSearch(query);
	};

	const reset = () => {
		setQuery('');
		inputRef.current?.focus({ preventScroll: true });
	};

	const onClearRecent = () => {
		clearRecentSearches();
		setRecent([]);
	};

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }--typing`]: isTyping,
	});

	return (
		<div className={rootClassnames}>
			<form role={'search'} action={'/search'} className={`${ ns }__form`} onSubmit={handleSubmit}>
				<input
					ref={inputRef}
					type={'text'}
					value={query}
					aria-label={'Search'}
					placeholder={'Search'}
					onChange={e => setQuery(e.target.value)}
				/>
				{!!query && (
					<button type={'reset'} className={`${ ns }__clear`} onClick={reset}>clear</button>
				)}
				<button type={'submit'} className={`${ ns }__submit`}>
					<span aria-hidden>{renderIcon('search')}</span>
					<span className={`visually-hidden`}>Search</span>
				</button>
			</form>

			<div
				ref={panelRef}
				onScroll={updateScrollHint}
				onLoadCapture={updateScrollHint}
				className={classNames({
					[`${ ns }__panel`]: true,
					[`${ ns }__panel--idle`]: !isTyping,
				})}
			>
				{isTyping ? (
					<>
						<div className={`${ ns }__col`}>
							<p className={`${ ns }__col-title`}>Search Suggestions</p>
							{SUGGESTION_GROUPS.filter(({ key }) => suggestions[key].length).map(({ key, label }) => (
								<div key={key} className={`${ ns }__group`}>
									<p className={`${ ns }__group-title`}>{label}</p>
									<ul>
										{suggestions[key].map(suggestion => (
											<li key={suggestion.title}>
												<button
													className={`${ ns }__term`}
													aria-label={suggestion.title}
													onClick={() => runSearch(suggestion.title, key)}
												>
													<span dangerouslySetInnerHTML={{ __html: suggestion.highlighted }} />
												</button>
											</li>
										))}
									</ul>
								</div>
							))}
							{!SUGGESTION_GROUPS.some(({ key }) => suggestions[key].length) && !loading && (
								<p className={`${ ns }__empty`}>No suggestions</p>
							)}
						</div>
						<div className={`${ ns }__col ${ ns }__col--products`}>
							<p className={`${ ns }__col-title`}>Product Suggestions</p>
							<ul>
								{products.map(product => (
									<li key={product.objectID}>
										<Link
											className={`${ ns }__product`}
											to={product.url}
											onClick={() => {
												sendProductClickEvent({
													objectID: product.objectID,
													queryID: product.queryID,
													index: PRODUCTS_INDEX,
													position: product.position,
												});
												storeSearchClickContext({
													objectID: product.objectID,
													productId: product.id,
													queryID: product.queryID,
													index: PRODUCTS_INDEX,
												});
												closeSearchFlyout();
											}}
										>
											{product.image && (
												<span className={`${ ns }__product-media`}>
													<img src={product.image} alt={product.title} />
												</span>
											)}
											<span className={`${ ns }__product-title`}>{product.title}</span>
										</Link>
									</li>
								))}
								{!products.length && !loading && (
									<li className={`${ ns }__empty`}>No products found</li>
								)}
							</ul>
						</div>
					</>
				) : (
					<>
						<div className={`${ ns }__col ${ ns }__col--recent`}>
							<p className={`${ ns }__col-title`}>Recent Searches</p>
							{!!recent.length && (
								<button className={`${ ns }__clear-all`} onClick={onClearRecent}>clear all</button>
							)}
							<ul>
								{recent.map(term => (
									<li key={term}>
										<button className={`${ ns }__term`} onClick={() => runSearch(term)}>{term}</button>
									</li>
								))}
								{!recent.length && (
									<li className={`${ ns }__empty`}>No recent searches</li>
								)}
							</ul>
						</div>
						<div className={`${ ns }__col`}>
							<p className={`${ ns }__col-title`}>Popular Searches</p>
							<ul>
								{POPULAR_SEARCHES.map(term => (
									<li key={term}>
										<button className={`${ ns }__term`} onClick={() => runSearch(term)}>{term}</button>
									</li>
								))}
							</ul>
						</div>
						<div className={`${ ns }__col ${ ns }__col--quick-links`}>
							<p className={`${ ns }__col-title`}>Quick Links</p>
							<ul>
								{QUICK_LINKS.map(({ label, url }) => (
									<li key={url}>
										<Link className={`${ ns }__term`} to={url} onClick={() => closeSearchFlyout()}>
											{label}
										</Link>
									</li>
								))}
							</ul>
						</div>
					</>
				)}
			</div>
			<ScrollHint visible={showScrollHint} onClick={handleScrollHintClick} />
		</div>
	);
};

export default SearchBar;
